import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { apiService, Gallery, Photo, GalleryResponse, PhotoResponse, PhotoPagination } from '../services/api';
import PhotoModal from '../components/PhotoModal';
import {
  albumKindFromId,
  buildDirectGallery,
  photosFromResponse,
  totalPhotosFromResponse,
} from '../utils/highlightsAlbum';

const Highlights: React.FC = () => {
  
  const { matchId } = useParams<{ matchId: string }>();
  const navigate = useNavigate();
  
  const [selectedGallery, setSelectedGallery] = useState<Gallery | null>(null);
  const [viewMode, setViewMode] = useState<'galleries' | 'photos'>('galleries');
  
  // Force initial load when component mounts
  useEffect(() => {
    
    if (!matchId) {
      
      fetchGalleries();
    } else {
      
    }
  }, []); // Empty dependency array - only run once on mount

  // Update page title based on current view
  useEffect(() => {
    if (viewMode === 'photos' && selectedGallery) {
      document.title = `${selectedGallery.title} - 心籃體育`;
    } else {
      document.title = '賽事精華 - 心籃體育';
    }
  }, [viewMode, selectedGallery]);
  const [galleries, setGalleries] = useState<Gallery[]>([]);
  const [galleryBuffer, setGalleryBuffer] = useState<Gallery[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  // Note: use isLoadingMore for photos; loadingMoreGalleries for galleries
  const [error, setError] = useState<string | null>(null);
  const [selectedPhotoIndex, setSelectedPhotoIndex] = useState<number>(0);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [photoPagination, setPhotoPagination] = useState<PhotoPagination | null>(null);
  const [currentPhotoPage, setCurrentPhotoPage] = useState<number>(1);
  const [allPhotosCache, setAllPhotosCache] = useState<Photo[]>([]);  // Store all photos for old format
  const [hasMorePhotos, setHasMorePhotos] = useState<boolean>(false);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const [showScrollToTop, setShowScrollToTop] = useState<boolean>(false);
  
  // Gallery pagination state
  const [galleryPagination, setGalleryPagination] = useState<{
    page: number;
    page_size: number;
    total_matches: number;
    total_pages: number;
    has_next: boolean;
    has_previous: boolean;
  } | null>(null);
  // Track per-source pagination to combine 12 items at a time across sources
  const [matchPage, setMatchPage] = useState<number>(1);
  const [eventPage, setEventPage] = useState<number>(1);
  const [hasMoreMatchGalleries, setHasMoreMatchGalleries] = useState<boolean>(false);
  const [hasMoreEventGalleries, setHasMoreEventGalleries] = useState<boolean>(false);
  
  // Individual backend totals for accurate display
  const [backendMatchTotal, setBackendMatchTotal] = useState<number>(0);
  const [backendEventTotal, setBackendEventTotal] = useState<number>(0);
  
  const [currentGalleryPage, setCurrentGalleryPage] = useState<number>(1);
  const [loadingMoreGalleries, setLoadingMoreGalleries] = useState<boolean>(false);
  
  // Sentinel for intersection observer (scroll-to-load for galleries)
  const galleryLoadMoreRef = useRef<HTMLDivElement | null>(null);
  // Sentinel for intersection observer (scroll-to-load for photos)
  const photoLoadMoreRef = useRef<HTMLDivElement | null>(null);
  // In-flight guards to prevent duplicate fetches
  const galleryLoadingRef = useRef<boolean>(false);
  const photoLoadingRef = useRef<boolean>(false);
  // Tracks whether the user has scrolled since entering the view to avoid eager auto-loads
  const hasUserScrolledRef = useRef<boolean>(false);

  // Scroll handler: primarily UI, but also a guarded fallback fetch if IO doesn't fire
  useEffect(() => {
    const handleScroll = () => {
      // More reliable scroll detection for both mobile and desktop
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      const windowHeight = window.innerHeight;
      const documentHeight = Math.max(
        document.body.scrollHeight,
        document.body.offsetHeight,
        document.documentElement.clientHeight,
        document.documentElement.scrollHeight,
        document.documentElement.offsetHeight
      );
      
      // Mark that the user has scrolled at least a bit
      if (scrollTop > 0 && !hasUserScrolledRef.current) {
        hasUserScrolledRef.current = true;
      }

      // No auto-fetch fallback here to avoid eager loading; rely on IntersectionObserver only

      // Control UI such as the scroll-to-top visibility
      // Show scroll-to-top button when user is near the bottom
      if (viewMode === 'photos' && photos.length > 0) {
        const isNearBottom = scrollTop + windowHeight >= documentHeight - 120; // show only when fairly close
        setShowScrollToTop(isNearBottom);
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [viewMode, photos.length]);

  // IntersectionObserver to auto-load more galleries when sentinel enters viewport
  useEffect(() => {
    // Only observe in galleries view
    if (viewMode !== 'galleries') return;
    const sentinel = galleryLoadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          entry.isIntersecting &&
          galleryPagination?.has_next &&
          !loadingMoreGalleries &&
          !galleryLoadingRef.current
        ) {
          // Trigger next page load
          loadMoreGalleries();
        }
      },
      {
        root: null,
        rootMargin: '60px 0px', // load much closer to bottom
        threshold: 0.25,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [viewMode, galleryPagination?.has_next, loadingMoreGalleries, currentGalleryPage]);

  // IntersectionObserver to auto-load more photos when sentinel enters viewport
  useEffect(() => {
    if (viewMode !== 'photos') return;
    const sentinel = photoLoadMoreRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (
          hasUserScrolledRef.current &&
          entry.isIntersecting &&
          hasMorePhotos &&
          !isLoadingMore &&
          !photoLoadingRef.current
        ) {
          loadMorePhotos();
        }
      },
      {
        root: null,
        rootMargin: '40px 0px', // only when very close to bottom
        threshold: 0.25,
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [viewMode, hasMorePhotos, isLoadingMore, currentPhotoPage]);

  // (Removed duplicate initial-load effect to avoid double fetching that could cause flicker)

  // Monitor galleryPagination state changes
  useEffect(() => {
    
  }, [galleryPagination]);
  
  // Monitor individual backend totals
  useEffect(() => {
    
  }, [backendMatchTotal, backendEventTotal]);

  // Handle URL-based navigation
  useEffect(() => {
    
    if (matchId && viewMode === 'galleries') {
      // If we have a matchId in URL and we're in galleries mode, load that specific gallery
      handleGalleryFromUrl(matchId);
    } else if (!matchId && viewMode === 'galleries' && galleries.length === 0) {
      // Otherwise, load all galleries only if we don't have any
      fetchGalleries();
    }
  }, [matchId, viewMode]); // Removed galleries.length dependency to prevent infinite loops

  const fetchGalleries = async (page: number = 1, append: boolean = false) => {
    
    
    // Prevent multiple simultaneous calls
    if (galleryLoadingRef.current && !append) {
      
      return;
    }
    
    try {
      if (!append) {
        setLoading(true);
        galleryLoadingRef.current = true;
      }
      
      // Fetch both match and event galleries
      
      let matchData;
      try {
        matchData = await apiService.getMatchGalleries(page, 12);
        
      } catch (err) {
        
        matchData = { galleries: [], pagination: { page: page, page_size: 12, total_matches: 0, total_pages: 0, has_next: false, has_previous: false } };
      }
      
      
      let eventData;
      try {
        eventData = await apiService.getEventGalleries(page, 12);
        
      } catch (err) {
        
        eventData = { galleries: [], pagination: { page: page, page_size: 12, total_matches: 0, total_pages: 0, has_next: false, has_previous: false } };
      }
      
      // Combine and sort galleries by date (newest first)
      const allGalleries = [...matchData.galleries, ...eventData.galleries];
      allGalleries.sort((a, b) => {
        const dateA = a.date ? new Date(a.date).getTime() : 0;
        const dateB = b.date ? new Date(b.date).getTime() : 0;
        return dateB - dateA;
      });
      
      // Always display only 12 galleries at a time; keep the rest in a buffer
      const initialVisible = allGalleries.slice(0, 12);
      const remainderBuffer = allGalleries.slice(12);
      setGalleries(append ? (prev => {
        const existingIds = new Set((prev as Gallery[]).map(g => g.id));
        const uniqueNew = initialVisible.filter(g => !existingIds.has(g.id));
        // @ts-ignore - React will resolve function state correctly
        return [...(prev as Gallery[]), ...uniqueNew];
      }) : initialVisible);
      setGalleryBuffer(append ? (prev => {
        // Append to existing buffer, de-dupe by id
        const existingIds = new Set((prev as Gallery[]).map(g => g.id));
        const uniqueNew = remainderBuffer.filter(g => !existingIds.has(g.id));
        // @ts-ignore
        return [...(prev as Gallery[]), ...uniqueNew];
      }) : remainderBuffer);
      
      // Use actual totals from backend APIs for accurate counting
      const totalMatchGalleries = matchData.pagination.total_matches || 0;
      const totalEventGalleries = eventData.pagination.total_matches || 0;
      const actualTotal = totalMatchGalleries + totalEventGalleries;
      
      // Store individual backend totals for accurate display
      setBackendMatchTotal(totalMatchGalleries);
      setBackendEventTotal(totalEventGalleries);
      
      // Determine if there are more galleries to load
      const matchHasNext = matchData.pagination.has_next;
      const eventHasNext = eventData.pagination.has_next;
      setHasMoreMatchGalleries(matchHasNext);
      setHasMoreEventGalleries(eventHasNext);
      setMatchPage(page);
      setEventPage(page);
      const hasMore = remainderBuffer.length > 0 || matchHasNext || eventHasNext;
      
      const combinedPagination = {
        page: page,
        page_size: 12,
        total_matches: actualTotal,
        total_pages: Math.ceil(actualTotal / 12),
        has_next: hasMore,
        has_previous: page > 1
      };
      
      setGalleryPagination(combinedPagination);
      setCurrentGalleryPage(page);
      
      setError(null);
    } catch (err: any) {
      setError(`Failed to load galleries: ${err.message}`);
    } finally {
      setLoading(false);
      if (!append) {
        galleryLoadingRef.current = false;
      }
    }
  };

  const loadMoreGalleries = async () => {
    
    if (galleryLoadingRef.current || loadingMoreGalleries || !galleryPagination?.has_next) {
      
      return;
    }
    
    try {
      galleryLoadingRef.current = true;
      setLoadingMoreGalleries(true);
      const nextCombinedPage = currentGalleryPage + 1;

      // Build up a buffer with at least 12 new items by fetching from sources as needed
      let workingBuffer: Gallery[] = [...galleryBuffer];
      let nextMatchPageLocal = matchPage;
      let nextEventPageLocal = eventPage;
      let matchHasNext = hasMoreMatchGalleries;
      let eventHasNext = hasMoreEventGalleries;

      while (workingBuffer.length < 12 && (matchHasNext || eventHasNext)) {
        // Fetch next match page if available
        if (matchHasNext) {
          try {
            const m = await apiService.getMatchGalleries(nextMatchPageLocal + 1, 12);
            nextMatchPageLocal += 1;
            matchHasNext = m.pagination.has_next;
            workingBuffer = [...workingBuffer, ...m.galleries];
          } catch (e) {
            matchHasNext = false;
          }
        }
        // Fetch next event page if available and still need items
        if (workingBuffer.length < 12 && eventHasNext) {
          try {
            const ev = await apiService.getEventGalleries(nextEventPageLocal + 1, 12);
            nextEventPageLocal += 1;
            eventHasNext = ev.pagination.has_next;
            workingBuffer = [...workingBuffer, ...ev.galleries];
          } catch (e) {
            eventHasNext = false;
          }
        }
        // Sort buffer every iteration to maintain newest-first ordering
        workingBuffer.sort((a, b) => {
          const dateA = a.date ? new Date(a.date).getTime() : 0;
          const dateB = b.date ? new Date(b.date).getTime() : 0;
          return dateB - dateA;
        });
      }

      // Take exactly 12 to display, keep remainder for next load
      const nextChunk = workingBuffer.slice(0, 12);
      const leftover = workingBuffer.slice(12);

      setGalleries(prev => {
        const existingIds = new Set(prev.map(g => g.id));
        const uniqueNew = nextChunk.filter(g => !existingIds.has(g.id));
        return [...prev, ...uniqueNew];
      });
      setGalleryBuffer(leftover);

      // Update per-source and combined pagination info
      setMatchPage(nextMatchPageLocal);
      setEventPage(nextEventPageLocal);
      setHasMoreMatchGalleries(matchHasNext);
      setHasMoreEventGalleries(eventHasNext);

      // Total remains the sum from the last known backend totals
      const actualTotal = (backendMatchTotal || 0) + (backendEventTotal || 0);
      const hasMore = leftover.length > 0 || matchHasNext || eventHasNext;

      const combinedPagination = {
        page: nextCombinedPage,
        page_size: 12,
        total_matches: actualTotal,
        total_pages: Math.ceil(actualTotal / 12),
        has_next: hasMore,
        has_previous: nextCombinedPage > 1
      };

      setGalleryPagination(combinedPagination);
      setCurrentGalleryPage(nextCombinedPage);
      
    } catch (err) {
      
      setError('Failed to load more galleries');
    } finally {
      // Delay unsetting the flags slightly to avoid rapid re-trigger by IO + scroll
      setTimeout(() => {
        setLoadingMoreGalleries(false);
        galleryLoadingRef.current = false;
      }, 150);
    }
  };

  const applyPhotoResponse = (photoResponse: Photo[] | PhotoResponse) => {
    if (Array.isArray(photoResponse)) {
      setAllPhotosCache(photoResponse);
      setPhotos(photoResponse.slice(0, 12));
      setPhotoPagination({
        page: 1,
        page_size: 12,
        total_photos: photoResponse.length,
        total_pages: Math.ceil(photoResponse.length / 12),
        has_next: photoResponse.length > 12,
        has_previous: false
      });
      setHasMorePhotos(photoResponse.length > 12);
    } else {
      setAllPhotosCache([]);
      setPhotos(photoResponse.photos || []);
      setPhotoPagination(photoResponse.pagination || null);
      setHasMorePhotos(Boolean(photoResponse.pagination?.has_next));
    }
    setCurrentPhotoPage(1);
  };

  const handleGalleryFromUrl = async (galleryId: string) => {
    try {
      setLoading(true);

      let gallery: Gallery | null = galleries.find(g => g.id === galleryId) || null;
      let isEvent = albumKindFromId(galleryId, gallery?.folderPath) === 'event';
      let photoResponse: Photo[] | PhotoResponse | null = null;

      if (!gallery) {
        const [matchResp, eventResp] = await Promise.all([
          apiService.getMatchGalleries(1, 12),
          apiService.getEventGalleries(1, 12)
        ]);
        const combined = [...matchResp.galleries, ...eventResp.galleries];
        gallery = combined.find(g => g.id === galleryId) || null;
        if (gallery) {
          isEvent = albumKindFromId(galleryId, gallery.folderPath) === 'event';
          setGalleries(combined);
        }
      }

      // Match page navigates with match.id, which is often not on galleries page 1.
      // Load photos by id directly instead of treating a missing listing as an empty event album.
      if (!gallery) {
        const preferredIsEvent = albumKindFromId(galleryId) === 'event';
        const tryPreferred = () => (
          preferredIsEvent
            ? apiService.getEventPhotos(galleryId, 1, 12)
            : apiService.getMatchPhotos(galleryId, 1, 12)
        );
        const tryFallback = () => (
          preferredIsEvent
            ? apiService.getMatchPhotos(galleryId, 1, 12)
            : apiService.getEventPhotos(galleryId, 1, 12)
        );

        try {
          photoResponse = await tryPreferred();
          isEvent = preferredIsEvent;
        } catch {
          photoResponse = null;
        }

        if (photosFromResponse(photoResponse).length === 0) {
          try {
            const fallbackResponse = await tryFallback();
            if (photosFromResponse(fallbackResponse).length > 0) {
              photoResponse = fallbackResponse;
              isEvent = !preferredIsEvent;
            } else if (!photoResponse) {
              photoResponse = fallbackResponse;
            }
          } catch {
            // Keep the preferred response when fallback fails.
          }
        }

        if (!photoResponse) {
          setError('Gallery not found');
          return;
        }

        let title: string | undefined;
        let description: string | undefined;
        let date: string | undefined;
        if (!isEvent) {
          try {
            const match = await apiService.getMatchDetail(galleryId);
            const team1 = match.team1?.name;
            const team2 = match.team2?.name;
            if (team1 && team2) {
              title = `${team1} vs ${team2}`;
              description = [match.year, match.group, match.match_no != null ? `場次${match.match_no}` : '']
                .filter(Boolean)
                .join(' ');
            }
            if (match.date) {
              date = match.date;
            }
          } catch {
            // Keep id-based title when match metadata is unavailable.
          }
        }

        gallery = buildDirectGallery(
          galleryId,
          isEvent ? 'event' : 'match',
          photosFromResponse(photoResponse),
          totalPhotosFromResponse(photoResponse),
          { title, description, date }
        );
      }

      setSelectedGallery(gallery);
      setViewMode('photos');

      if (!photoResponse) {
        photoResponse = isEvent
          ? await apiService.getEventPhotos(gallery.id, 1, 12)
          : await apiService.getMatchPhotos(gallery.id, 1, 12);
      }

      applyPhotoResponse(photoResponse);
      setError(null);
    } catch (err) {
      setError('Failed to load photos');
    } finally {
      setLoading(false);
    }
  };

  const handleGalleryClick = async (gallery: Gallery) => {
    try {
      setLoading(true);
      setSelectedGallery(gallery);
      setViewMode('photos');
      // Ensure we start at the top when entering an album
      window.scrollTo({ top: 0, behavior: 'auto' });
      hasUserScrolledRef.current = false;
      
      // Update URL
      navigate(`/highlights/${gallery.id}`);
      
      // Determine gallery type and fetch photos accordingly
      let photoResponse: any;
      const looksLikeEvent = Boolean(
        (gallery.folderPath && gallery.folderPath.includes('/events/')) ||
        /^\d{4}_.+/.test(gallery.id)
      );
      try {
        photoResponse = looksLikeEvent
          ? await apiService.getEventPhotos(gallery.id, 1, 12)
          : await apiService.getMatchPhotos(gallery.id, 1, 12);
      } catch (e) {
        // Fallback: if first attempt fails, try the other endpoint
        
        photoResponse = looksLikeEvent
          ? await apiService.getMatchPhotos(gallery.id, 1, 12)
          : await apiService.getEventPhotos(gallery.id, 1, 12);
      }
      
      // Handle both old (array) and new (paginated) response formats
      if (Array.isArray(photoResponse)) {
        // Old format - flat array of all photos
        setPhotos(photoResponse.slice(0, 12)); // Take first 12
        setPhotoPagination({
          page: 1,
          page_size: 12,
          total_photos: photoResponse.length,
          total_pages: Math.ceil(photoResponse.length / 12),
          has_next: photoResponse.length > 12,
          has_previous: false
        });
        setHasMorePhotos(photoResponse.length > 12);
        setAllPhotosCache(photoResponse);
      } else {
        // New format - paginated response
        setPhotos(photoResponse.photos);
        setPhotoPagination(photoResponse.pagination);
        setHasMorePhotos(photoResponse.pagination.has_next);
        setAllPhotosCache([]);
      }
      setCurrentPhotoPage(1);
      // Make sure the viewport stays at the top after content renders
      setTimeout(() => {
        window.scrollTo({ top: 0, behavior: 'auto' });
      }, 0);
      setError(null);
    } catch (err) {
      
      setError('Failed to load photos');
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  };

  const handleBackToGalleries = () => {
    setSelectedGallery(null);
    setViewMode('galleries');
    setPhotos([]);
    setPhotoPagination(null);
    setCurrentPhotoPage(1);
    setAllPhotosCache([]);
    setHasMorePhotos(false);
    setIsLoadingMore(false);
    setSelectedPhotoIndex(0);
    setIsModalOpen(false);
    setError(null);
    hasUserScrolledRef.current = false;
    window.scrollTo({ top: 0, behavior: 'auto' });
    navigate('/highlights', { replace: true });
  };

  const loadMorePhotos = async () => {
    if (!selectedGallery || !hasMorePhotos || isLoadingMore || photoLoadingRef.current) return;

    try {
      photoLoadingRef.current = true;
      setIsLoadingMore(true);
      const nextPage = currentPhotoPage + 1;
      let photoResponse: any;
      // Check if this is an event gallery by looking at the folder path or title
      if (selectedGallery.folderPath && selectedGallery.folderPath.includes('/events/')) {
        photoResponse = await apiService.getEventPhotos(selectedGallery.id, nextPage, 12);
      } else {
        photoResponse = await apiService.getMatchPhotos(selectedGallery.id, nextPage, 12);
      }
      
      if (Array.isArray(photoResponse)) {
        // Old format - simulate pagination with all photos
        const startIndex = (nextPage - 1) * 12;
        const endIndex = startIndex + 12;
        const newPhotos = photoResponse.slice(startIndex, endIndex);
        
        setPhotos(prevPhotos => {
          // Prevent duplicate photos by checking IDs
          const existingIds = new Set(prevPhotos.map(p => p.id));
          const uniqueNewPhotos = newPhotos.filter(photo => !existingIds.has(photo.id));
          return [...prevPhotos, ...uniqueNewPhotos];
        });
        setPhotoPagination(prev => prev ? {
          ...prev,
          page: nextPage,
          has_next: endIndex < photoResponse.length,
          has_previous: nextPage > 1
        } : null);
        setHasMorePhotos(endIndex < photoResponse.length);
      } else {
        // New format - use API pagination
        setPhotos(prevPhotos => {
          // Prevent duplicate photos by checking IDs
          const existingIds = new Set(prevPhotos.map(p => p.id));
          const uniqueNewPhotos = photoResponse.photos.filter((photo: any) => !existingIds.has(photo.id));
          return [...prevPhotos, ...uniqueNewPhotos];
        });
        setPhotoPagination(photoResponse.pagination);
        setHasMorePhotos(photoResponse.pagination.has_next);
      }
      
      setCurrentPhotoPage(nextPage);
    } catch (err) {
      
      setError('Failed to load more photos');
    } finally {
      setIsLoadingMore(false);
      photoLoadingRef.current = false;
    }
  };

  const handlePhotoClick = (photo: Photo) => {
    const photoIndex = photos.findIndex(p => p.id === photo.id);
    setSelectedPhotoIndex(photoIndex >= 0 ? photoIndex : 0);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedPhotoIndex(0);
  };

  const handleNavigate = async (direction: 'next' | 'prev') => {
    if (direction === 'next') {
      if (selectedPhotoIndex < photos.length - 1) {
        // Move to next photo in current batch
        setSelectedPhotoIndex(selectedPhotoIndex + 1);
      } else if (hasMorePhotos && !isLoadingMore && !photoLoadingRef.current) {
        // At end of current batch, load more photos
        await loadMorePhotos();
        // After loading, move to the next photo
        setSelectedPhotoIndex(selectedPhotoIndex + 1);
      }
      // If no more photos available, stay at current position (don't wrap to 0)
    } else if (direction === 'prev' && selectedPhotoIndex > 0) {
      setSelectedPhotoIndex(selectedPhotoIndex - 1);
    }
  };

  const handleScrollToTop = () => {
    window.scrollTo({
      top: 0,
      behavior: 'smooth'
    });
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '20px', backgroundColor: '#e3f2fd', minHeight: '100vh' }}>

      
      {/* Top Navigation - Only show when viewing photos */}
      {viewMode === 'photos' && (
        <div style={{ 
          marginBottom: '24px',
          display: 'flex',
          alignItems: 'center',
          gap: '16px'
        }}>
          <button
            onClick={handleBackToGalleries}
            style={{
              backgroundColor: 'transparent',
              color: '#3182ce',
              border: '1px solid #3182ce',
              padding: '8px 16px',
              borderRadius: '6px',
              fontSize: '14px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            ← 返回
          </button>
        </div>
      )}

      {/* Header */}
      <div style={{ 
        marginBottom: '32px',
        textAlign: 'center'
      }}>
        <h1 style={{ 
          fontSize: '2.5rem', 
          color: '#2d3748',
          marginBottom: '8px'
        }}>
          {viewMode === 'photos' && selectedGallery ? selectedGallery.title : '賽事精華'}
        </h1>
        <p style={{
          fontSize: '1rem',
          color: '#4a5568',
          marginBottom: '24px'
        }}>
          {viewMode === 'photos' && selectedGallery ? selectedGallery.description : '查看賽事精華'}
        </p>
      </div>

      {/* Gallery, Photo, or Album View */}
      {viewMode === 'galleries' ? (
        <>


          {/* Gallery Summary */}
          {!loading && !error && galleries.length > 0 && (
            <div style={{
              textAlign: 'center',
              marginBottom: '24px',
              color: '#4a5568',
              fontSize: '14px'
            }}>
                        {(() => {
            // Use ONLY backend API totals: prefer combined pagination if present
            const totalFromBackend = (galleryPagination?.total_matches ?? (backendMatchTotal + backendEventTotal));
            
            // Show loading state if backend totals aren't set yet
            if (loading || (backendMatchTotal === 0 && backendEventTotal === 0)) {
              return `載入中... (已載入 ${galleries.length} 個相簿)`;
            }
            
            // Force display the actual values for debugging
            return `共 ${totalFromBackend} 個相簿 • ${backendMatchTotal} 個比賽相簿 • ${backendEventTotal} 個活動相簿 (已載入 ${galleries.length} 個)`;
          })()}
            </div>
          )}

          {/* Gallery Grid */}
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '1.2rem', color: '#4a5568' }}>載入中...</div>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '1.2rem', color: '#e53e3e' }}>{error}</div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
              gap: '24px',
              marginBottom: '40px'
            }}>
              {galleries.map((gallery) => (
                <div
                  key={gallery.id}
                  style={{
                    backgroundColor: '#e3f2fd',
                    borderRadius: '12px',
                    overflow: 'hidden',
                    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'pointer',
                    position: 'relative'
                  }}
                  onClick={() => handleGalleryClick(gallery)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-4px)';
                    e.currentTarget.style.boxShadow = '0 8px 25px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 6px rgba(0,0,0,0.1)';
                  }}
                >
                  {/* Gallery Type Badge */}
                  <div style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    backgroundColor: gallery.folderPath && gallery.folderPath.includes('/events/') ? '#9b59b6' : '#3182ce',
                    color: 'white',
                    padding: '4px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '600',
                    zIndex: 1
                  }}>
                    {gallery.folderPath && gallery.folderPath.includes('/events/') ? '活動' : '比賽'}
                  </div>

                  {/* Cover Image */}
                  <div style={{
                    width: '100%',
                    height: '250px',
                    backgroundColor: '#f7fafc',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    {gallery.coverImage ? (
                      <img
                        src={gallery.coverImage}
                        alt={gallery.title}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          transition: 'transform 0.3s ease'
                        }}
                        onError={(e) => {
                          // Fallback for missing images
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = `
                            <div style="
                              display: flex;
                              align-items: center;
                              justify-content: center;
                              height: 100%;
                              color: #a0aec0;
                              font-size: 3rem;
                            ">
                              🏀
                            </div>
                          `;
                        }}
                      />
                    ) : (
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        height: '100%',
                        color: '#a0aec0',
                        fontSize: '3rem'
                      }}>
                        🏀
                      </div>
                    )}

                  </div>

                  {/* Content */}
                  <div style={{ padding: '20px' }}>
                    <h3 style={{
                      fontSize: '1.2rem',
                      color: '#2d3748',
                      marginBottom: '8px',
                      fontWeight: '600'
                    }}>
                      {gallery.title}
                    </h3>
                    <p style={{
                      fontSize: '0.9rem',
                      color: '#4a5568',
                      lineHeight: '1.5',
                      marginBottom: '12px'
                    }}>
                      {gallery.description}
                    </p>
                    <div style={{
                      fontSize: '0.8rem',
                      color: '#718096',
                      fontStyle: 'italic'
                    }}>
                      {gallery.date && !isNaN(new Date(gallery.date).getTime()) ? (
                        new Date(gallery.date).toLocaleDateString('zh-TW', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })
                      ) : (
                        '未定'
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Load More Galleries Button - Hidden, using automatic scroll-to-load instead */}
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            {/* Button hidden - automatic scroll-to-load is enabled */}

            {/* Invisible sentinel used by IntersectionObserver */}
            <div ref={galleryLoadMoreRef} style={{ height: 1 }} />
          </div>

          {/* Gallery Count Info */}
          {galleryPagination && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: '16px',
              fontSize: '0.9rem',
              color: '#718096'
            }}>
              顯示 {galleries.length} / {galleryPagination.total_matches} 個相簿
              {galleryPagination.total_pages > 1 && (
                <span style={{ marginLeft: '10px', color: '#3182ce' }}>
                  (第 {galleryPagination.page} 頁，共 {galleryPagination.total_pages} 頁)
                </span>
              )}
              {loadingMoreGalleries && (
                <span style={{ marginLeft: '10px', color: '#3182ce' }}>
                  🔄 載入更多中...
                </span>
              )}
            </div>
          )}
          
        </>
      ) : viewMode === 'photos' && selectedGallery ? (
        <>


          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '1.2rem', color: '#4a5568' }}>載入照片中...</div>
            </div>
          ) : error ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>
              <div style={{ fontSize: '1.2rem', color: '#e53e3e' }}>{error}</div>
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
              gap: '16px',
              marginBottom: '40px'
            }}>
              {photos.map((photo) => (
                <div
                  key={photo.id}
                  style={{
                    backgroundColor: '#e3f2fd',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    cursor: 'pointer'
                  }}
                  onClick={() => handlePhotoClick(photo)}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = 'translateY(-2px)';
                    e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 2px 4px rgba(0,0,0,0.1)';
                  }}
                >
                  {/* Photo */}
                  <div style={{
                    width: '100%',
                    height: '200px',
                    backgroundColor: '#f7fafc',
                    position: 'relative',
                    overflow: 'hidden'
                  }}>
                    <img
                      src={photo.imageUrl}
                      alt={photo.title}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.3s ease'
                      }}
                      onError={(e) => {
                        // Fallback for missing images
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        target.parentElement!.innerHTML = `
                          <div style="
                            display: flex;
                            align-items: center;
                            justify-content: center;
                            height: 100%;
                            color: #a0aec0;
                            font-size: 2rem;
                          ">
                            📷
                          </div>
                        `;
                      }}
                    />
                  </div>


                </div>
              ))}
            </div>
          )}

  {/* Load More Photos Button + Sentinel */}
          <div style={{ textAlign: 'center', marginTop: '24px' }}>
            {photoPagination && photoPagination.has_next && (
              <button 
                style={{
          backgroundColor: isLoadingMore ? '#a0aec0' : '#3182ce',
                  color: 'white',
                  border: 'none',
                  padding: '12px 24px',
                  borderRadius: '6px',
                  fontSize: '16px',
          cursor: isLoadingMore ? 'not-allowed' : 'pointer',
                  transition: 'background-color 0.2s ease',
          opacity: isLoadingMore ? 0.7 : 1
                }}
                onClick={loadMorePhotos}
        disabled={isLoadingMore}
                onMouseEnter={(e) => {
          if (!isLoadingMore) {
                    e.currentTarget.style.backgroundColor = '#2c5aa0';
                  }
                }}
                onMouseLeave={(e) => {
          if (!isLoadingMore) {
                    e.currentTarget.style.backgroundColor = '#3182ce';
                  }
                }}
              >
        {isLoadingMore ? '載入中...' : '載入更多照片'}
              </button>
            )}
            {/* Invisible sentinel used by IntersectionObserver for photos */}
            <div ref={photoLoadMoreRef} style={{ height: 1 }} />
          </div>

          {/* Photo Count Info */}
          {photoPagination && (
            <div style={{ 
              textAlign: 'center', 
              marginTop: '16px',
              fontSize: '0.9rem',
              color: '#718096'
            }}>
              顯示 {Math.min(photos.length, photoPagination.total_photos)} / {photoPagination.total_photos} 張照片
              {photos.length > photoPagination.total_photos && (
                <div style={{ 
                  marginTop: '8px',
                  fontSize: '0.8rem',
                  color: '#e53e3e'
                }}>
                  ⚠️ 檢測到重複照片，正在清理...
                </div>
              )}
              {isLoadingMore && (
                <span style={{ marginLeft: '10px', color: '#3182ce' }}>
                  🔄 載入更多中...
                </span>
              )}
            </div>
          )}
        </>
      ) : null}

      {/* Photo Modal */}
      <PhotoModal
        photos={photos}
        currentIndex={selectedPhotoIndex}
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onNavigate={handleNavigate}
        totalPhotos={photoPagination?.total_photos || photos.length}
      />

      {/* Scroll to Top Button */}
      {viewMode === 'photos' && photos.length > 0 && !isModalOpen && (
        <button
          onClick={handleScrollToTop}
          style={{
            position: 'fixed',
            bottom: '20px',
            right: '20px',
            backgroundColor: '#3182ce',
            color: 'white',
            border: 'none',
            borderRadius: '50%',
            width: '50px',
            height: '50px',
            fontSize: '20px',
            cursor: 'pointer',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            transition: 'all 0.2s ease',
            zIndex: 1000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#2c5aa0';
            e.currentTarget.style.transform = 'scale(1.1)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = '#3182ce';
            e.currentTarget.style.transform = 'scale(1)';
          }}
        >
          ↑
        </button>
      )}
    </div>
  );
};

export default Highlights; 